import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../App.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { ReactComponent as LinkedInIcon } from '../ressources/linkedin.svg';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

const LEFT_CLICK_ZONE = 0.5;
const RIGHT_CLICK_ZONE = 0.5;

const newsletterValidationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Required'),
});

const messageValidationSchema = Yup.object({
  firstname: Yup.string().required('Required'),
  lastname: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email address').required('Required'),
  company: Yup.string(),
  subject: Yup.string().required('Required'),
  text: Yup.string().required('Required'),
});

function App() {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isFollowPopupOpen, setIsFollowPopupOpen] = useState(false);
  const [isMessagePopupOpen, setIsMessagePopupOpen] = useState(false);
  const [isSocialPopupOpen, setIsSocialPopupOpen] = useState(false); 
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState([]);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const popupRef = useRef(null); 
  const followPopupRef = useRef(null); 
  const messagePopupRef = useRef(null);
  const socialPopupRef = useRef(null);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await axios.get('https://leoleveque.net/api/stories/1');
        setImages(response.data.images);
      } catch (error) {
        console.error('Error fetching images:', error);
      }
    };

    fetchImages();
  }, []);

  const openPopup = () => {
    setIsPopupOpen(true);
    setIsSuccess(false);
  };

  const closePopup = useCallback(() => {
    setIsPopupOpen(false);
    setCurrentImageIndex(prevIndex => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
  }, [images.length]);

  const nextImage = () => {
    setCurrentImageIndex(prevIndex => {
      if (prevIndex < images.length - 1) {
        return prevIndex + 1;
      } else {
        closePopup();
        return -1;
      }
    });
  };

  const openFollowPopup = () => {
    setIsFollowPopupOpen(true);
    setIsSuccess(false);
  };

  const openMessagePopup = () => {
    setIsMessagePopupOpen(true);
    setIsSuccess(false);
  };

  const openSocialPopup = () => { 
    setIsSocialPopupOpen(true);
  };

  const closeFollowPopup = useCallback(() => setIsFollowPopupOpen(false), []);
  const closeMessagePopup = useCallback(() => setIsMessagePopupOpen(false), []);
  const closeSocialPopup = useCallback(() => setIsSocialPopupOpen(false), []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      if (isFollowPopupOpen && !followPopupRef.current.contains(e.target)) {
        closeFollowPopup();
      }
      if (isMessagePopupOpen && !messagePopupRef.current.contains(e.target)) {
        closeMessagePopup();
      }
      if (isSocialPopupOpen && !socialPopupRef.current.contains(e.target)) {
        closeSocialPopup();
      }
      if (isPopupOpen && !popupRef.current.contains(e.target)) {
        closePopup();
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isFollowPopupOpen && followPopupRef.current && !followPopupRef.current.contains(e.target)) {
        closeFollowPopup();
      }
      if (isMessagePopupOpen && messagePopupRef.current && !messagePopupRef.current.contains(e.target)) {
        closeMessagePopup();
      }
      if (isSocialPopupOpen && socialPopupRef.current && !socialPopupRef.current.contains(e.target)) { 
        closeSocialPopup();
      }
      if (isPopupOpen && popupRef.current && !popupRef.current.contains(e.target)) {
        closePopup();
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
  
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFollowPopupOpen, isMessagePopupOpen, isPopupOpen, isSocialPopupOpen, closePopup, closeFollowPopup, closeMessagePopup, closeSocialPopup]);

  const handleImageClick = (e) => {
    e.stopPropagation();
    
    const imageWidth = e.currentTarget.offsetWidth;
    const clickX = e.nativeEvent.offsetX;
    
    if (clickX < (imageWidth * LEFT_CLICK_ZONE)) {
      setCurrentImageIndex(prevIndex => {
        if (prevIndex > 0) {
          return prevIndex - 1;
        } else {
          return 0;
        }
      });
    } else if (clickX > (imageWidth * RIGHT_CLICK_ZONE)) {
      nextImage();
    }
  };

  const progressWidth = ((currentImageIndex + 1) / images.length) * 100;

  return (
    <div className="App">
      <section id="about">
        <div className="profile-container">
          <img
            src={require('../ressources/pdp.jpg')}
            alt="Profile"
            className="profile-picture"
            onClick={openPopup}
          />
          <div className="profile-details">
            <h2>Léo Lévêque <span className="tag">@leoleveque.net</span></h2>
            <h3>Software engineer</h3>
            <p className="description">
              Passionate software developer with a love for creating sleek and performant applications. Focused on delivering excellent user experiences through efficient design and seamless functionality.
            </p>
            <div className="profile-actions">
              <button className="btn follow-btn" onClick={openFollowPopup}>Follow</button>
              <button className="btn message-btn" onClick={openMessagePopup}>Message</button>
              <button className="btn social-network-btn" onClick={openSocialPopup}>Social network</button>
            </div>
          </div>
        </div>
      </section>

      {isPopupOpen && images.length > 0 && (
        <div className="popup-overlay" onClick={handleOverlayClick}>
          <div className="popup-content" ref={popupRef}>
            <img
              src={images[currentImageIndex]}
              alt="Profile Large"
              className="popup-image"
              onClick={handleImageClick}
            />
            <div className="popup-indicator">
              <div
                className="progress-bar"
                style={{ width: `${progressWidth}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {isFollowPopupOpen && (
        <div className="popup-overlay" onClick={handleOverlayClick}>
          <div className="popup-content-btn" ref={followPopupRef}>
            {isLoading ? (
              <div className="spinner"></div>
            ) : isSuccess ? (
              <div className="success-message">
                <p>Subscription successful! You will be redirected shortly.</p>
              </div>
            ) : (
              <Formik
                initialValues={{ email: '' }}
                validationSchema={newsletterValidationSchema}
                onSubmit={async (values) => {
                  setIsLoading(true);
                  try {
                    await axios.post('https://leoleveque.net/api/subscribe', values);
                    setIsSuccess(true);
                    setTimeout(() => closeFollowPopup(), 2000);
                  } catch (error) {
                    console.error('There was an error subscribing to the newsletter:', error);
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                <Form>
                  <h2>Subscribe to our Newsletter</h2>
                  <label>Email:</label>
                  <Field type="email" name="email" />
                  <ErrorMessage name="email" component="div" className="error-message" />
                  <br />
                  <p className="privacy-text">
                    By subscribing, you accept our{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                      privacy policy
                    </a>.
                  </p>
                  <button type="submit">Subscribe</button>
                </Form>
              </Formik>
            )}
          </div>
        </div>
      )}

      {isMessagePopupOpen && (
        <div className="popup-overlay" onClick={handleOverlayClick}>
          <div className="popup-content-btn" ref={messagePopupRef}>
            {isLoading ? (
              <div className="spinner"></div>
            ) : isSuccess ? (
              <div className="success-message">
                <p>Message sent successfully! You will be redirected shortly.</p>
              </div>
            ) : (
              <Formik
                initialValues={{
                  firstname: '',
                  lastname: '',
                  email: '',
                  company: '',
                  subject: '',
                  text: '',
                }}
                validationSchema={messageValidationSchema}
                onSubmit={async (values) => {
                  setIsLoading(true);
                  try {
                    await axios.post('https://leoleveque.net/api/send-message', values);
                    setIsSuccess(true);
                    setTimeout(() => closeMessagePopup(), 2000);
                  } catch (error) {
                    console.error('There was an error sending the message:', error);
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                <Form>
                  <h2>Send a Message</h2>
                  <label>First Name:</label>
                  <Field type="text" name="firstname" />
                  <ErrorMessage name="firstname" component="div" className="error-message" />
                  <br />
                  <label>Last Name:</label>
                  <Field type="text" name="lastname" />
                  <ErrorMessage name="lastname" component="div" className="error-message" />
                  <br />
                  <label>Email:</label>
                  <Field type="email" name="email" />
                  <ErrorMessage name="email" component="div" className="error-message" />
                  <br />
                  <label>Company (Optional):</label>
                  <Field type="text" name="company" />
                  <br />
                  <label>Subject:</label>
                  <Field type="text" name="subject" />
                  <ErrorMessage name="subject" component="div" className="error-message" />
                  <br />
                  <label>Message:</label>
                  <Field as="textarea" name="text" />
                  <ErrorMessage name="text" component="div" className="error-message" />
                  <br />
                  <p className="privacy-text">
                    By sending this message, you accept our{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                      privacy policy
                    </a>.
                  </p>
                  <button type="submit">Send</button>
                </Form>
              </Formik>
            )}
          </div>
        </div>
      )}

      {isSocialPopupOpen && (
        <div className="popup-overlay" onClick={handleOverlayClick}>
          <div className="popup-content-btn" ref={socialPopupRef}>
            <h2>Connect with me</h2>
            <p>
              <a href="https://www.linkedin.com/in/leo-leveque-bb8bb2250/" target="_blank" rel="noopener noreferrer">
                <LinkedInIcon className="social-icon" style={{ width: '50px', height: '50px', color: '#0077b5' }} />
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
